import * as React from 'react';
import styled from 'styled-components';

import { StyledText } from './index';
import { ChevronRightDisabled, ChevronRightPrimary, ChevronRightSecondary } from '../assets/images/custom';
import { StyledTextVariantsTypeV2 } from './StyledText';
import theme from '../styles/theme';

export enum ButtonVariantsType {
    PRIMARY_GREEN = 'primaryGreen',
    PRIMARY_GREEN_LARGE = 'primaryGreenLarge',
    SECONDARY_GREEN = 'secondaryGreen',
    SECONDARY_GREEN_LARGE = 'secondaryGreenLarge',
    PRIMARY_BLUE = 'primaryBlue',
    PRIMARY_BLUE_LARGE = 'primaryBlueLarge',
    SECONDARY_BLUE = 'secondaryBlue',
    SECONDARY_BLUE_LARGE = 'secondaryBlueLarge',
    PRIMARY_TRANSPARENT = 'primaryTransparent',
}

enum ButtonHeight {
    REGULAR = '48px',
    LARGE = '52px',
}

interface IProps {
    text: string;
    onClick: (event: any) => void;
    variant: ButtonVariantsType;
    disabled?: boolean;
    width?: string;
    mobileWidth?: string;
    margin?: string;
    zIndex?: number | string;
    withArrow?: boolean;
    transparent?: boolean;
}

const styles = {
    primaryGreen: {
        backgroundColor: theme.colors.light.green.baseLight,
        backgroundColorHover: theme.colors.light.green.L20,
        backgroundColorPressed: theme.colors.light.green.D20,
        border: 'none',
        color: theme.constantStyles.colorStyles.white,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.REGULAR,
    },
    primaryGreenLarge: {
        backgroundColor: theme.colors.light.green.baseLight,
        backgroundColorHover: theme.colors.light.green.L20,
        backgroundColorPressed: theme.colors.light.green.D20,
        border: 'none',
        color: theme.constantStyles.colorStyles.white,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.LARGE,
    },
    secondaryGreen: {
        backgroundColor: theme.constantStyles.colorStyles.white,
        backgroundColorHover: theme.colors.light.green.L20,
        backgroundColorPressed: theme.colors.light.green.D20,
        border: `1px solid ${theme.colors.light.green.baseLight}`,
        color: theme.colors.light.green.baseLight,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.REGULAR,
    },
    secondaryGreenLarge: {
        backgroundColor: theme.constantStyles.colorStyles.white,
        backgroundColorHover: theme.colors.light.green.L20,
        backgroundColorPressed: theme.colors.light.green.D20,
        border: `1px solid ${theme.colors.light.green.baseLight}`,
        color: theme.colors.light.green.baseLight,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.LARGE,
    },

    primaryBlue: {
        backgroundColor: theme.colors.light.blue.baseLight,
        backgroundColorHover: theme.colors.light.blue.L20,
        backgroundColorPressed: theme.colors.light.blue.D20,
        border: 'none',
        color: theme.constantStyles.colorStyles.white,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.REGULAR,
    },
    primaryBlueLarge: {
        backgroundColor: theme.colors.light.blue.baseLight,
        backgroundColorHover: theme.colors.light.blue.L20,
        backgroundColorPressed: theme.colors.light.blue.D20,
        border: 'none',
        color: theme.constantStyles.colorStyles.white,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.LARGE,
    },
    secondaryBlue: {
        backgroundColor: theme.constantStyles.colorStyles.white,
        backgroundColorHover: theme.colors.light.blue.L20,
        backgroundColorPressed: theme.colors.light.blue.D20,
        border: `1px solid ${theme.colors.light.blue.baseLight}`,
        color: theme.colors.light.blue.baseLight,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.REGULAR,
    },
    secondaryBlueLarge: {
        backgroundColor: theme.constantStyles.colorStyles.white,
        backgroundColorHover: theme.colors.light.blue.L20,
        backgroundColorPressed: theme.colors.light.blue.D20,
        border: `1px solid ${theme.colors.light.blue.baseLight}`,
        color: theme.colors.light.blue.baseLight,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.LARGE,
    },
    primaryTransparent: {
        backgroundColor: 'transparent',
        backgroundColorHover: theme.colors.light.green.L20,
        backgroundColorPressed: 'transparent',
        border: `1px solid ${theme.constantStyles.colorStyles.white}`,
        color: theme.constantStyles.colorStyles.white,
        colorHover: theme.constantStyles.colorStyles.white,
        colorPressed: theme.constantStyles.colorStyles.white,
        height: ButtonHeight.REGULAR,
    },
    disabled: {
        border: `1px solid ${theme.colors.light.grayscale['400']}`,
        backgroundColor: theme.colors.light.grayscale['600'],
        color: theme.colors.light.grayscale['400'],
    },
};

const ButtonWrapper = styled.button<Omit<IProps, 'children'>>`
    background-color: ${(props: IProps) =>
        props.disabled
            ? styles.disabled.backgroundColor
            : props.transparent
            ? 'transparent'
            : styles[props.variant].backgroundColor};
    border: ${(props: IProps) => (props.disabled ? styles.disabled.border : styles[props.variant].border)};
    width: ${({ width }: IProps) => width};
    height: ${({ variant }: IProps) => styles[variant].height};
    margin: ${({ margin }: IProps) => margin};
    z-index: ${({ zIndex }: IProps) => zIndex};
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    white-space: nowrap;
    overflow: hidden;
    cursor: ${({ disabled }: IProps) => (disabled ? 'not-allowed' : 'pointer')};
    box-sizing: border-box;
    padding: 0 32px;

    img {
        height: 24px;
        width: 24px;
        margin-left: 7px;
    }

    :focus {
        outline: none;
    }

    div {
        cursor: ${({ disabled }: IProps) => (disabled ? 'not-allowed' : 'pointer')};
    }

    :hover {
        background-color: ${(props: IProps) =>
            props.disabled ? styles.disabled.backgroundColor : styles[props.variant].backgroundColorHover} !important;

        & > div {
            color: ${(props: IProps) =>
                props.disabled ? styles.disabled.color : styles[props.variant].colorHover} !important;
        }
    }

    :active {
        background-color: ${(props: IProps) =>
            props.disabled ? styles.disabled.backgroundColor : styles[props.variant].backgroundColorPressed} !important;

        & > div {
            color: ${(props: IProps) =>
                props.disabled ? styles.disabled.color : styles[props.variant].colorPressed} !important;
        }
    }

    @media only screen and (max-width: 540px) {
        width: ${({ mobileWidth }: IProps) => mobileWidth};
        margin-bottom: 3%;
    }
`;

const Button = (props: IProps) => {
    const arrowIcon = props.withArrow
        ? props.disabled
            ? ChevronRightDisabled
            : props.variant.indexOf('primary') > -1
            ? ChevronRightPrimary
            : props.variant.indexOf('secondary') > -1
            ? ChevronRightSecondary
            : null
        : null;

    return (
        <ButtonWrapper onClick={!props.disabled ? props.onClick : undefined} {...props}>
            <StyledText
                width={'100%'}
                textAlign={'center'}
                text={props.text}
                styles={{ whiteSpace: 'pre' }}
                variant={StyledTextVariantsTypeV2.BUTTON}
                margin="0% 5% 0% 0%"
                colorVariant={props.disabled ? styles.disabled.color : styles[props.variant].color}
            />
            {arrowIcon && <img src={arrowIcon} style={{ marginLeft: 8 }} alt={'button-arrow'} />}
        </ButtonWrapper>
    );
};

Button.defaultProps = {
    text: 'button',
    variant: ButtonVariantsType.PRIMARY_GREEN,
    disabled: false,
    withArrow: false,
    margin: 'unset',
    width: 'fit-content',
} as Partial<IProps>;

export default Button;
